import React, { useState } from 'react';
import PageHero from './PageHero';
import './Careers.css';

const Careers = () => {
  const [showResumeForm, setShowResumeForm] = useState(false);
  const [resumeForm, setResumeForm] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    message: ''
  });
  const [resumeFile, setResumeFile] = useState(null);

  const handleFormChange = (e) => {
    setResumeForm({
      ...resumeForm,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const validExtensions = ['.pdf', '.doc', '.docx'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        alert('Please upload a PDF or Word document (.pdf, .doc, .docx)');
        e.target.value = '';
        setResumeFile(null);
        return;
      }

      // Validate file size (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('File size must be less than 10MB. Please compress your resume and try again.');
        e.target.value = '';
        setResumeFile(null);
        return;
      }

      setResumeFile(file);
    }
  };

  const handleResumeSubmit = (e) => {
    e.preventDefault();
    
    if (!resumeFile) {
      alert('Please upload your resume');
      return;
    }

    // Open email client with pre-filled information
    const subject = encodeURIComponent(`Resume Submission${resumeForm.position ? ` - ${resumeForm.position}` : ''}`);
    const body = encodeURIComponent(
      `Name: ${resumeForm.name}\n` +
      `Email: ${resumeForm.email}\n` +
      `Phone: ${resumeForm.phone || 'Not provided'}\n` +
      `Position: ${resumeForm.position || 'General Application'}\n\n` +
      `Message:\n${resumeForm.message || 'No additional message provided'}\n\n` +
      `Please attach the resume file (${resumeFile.name}) to this email.`
    );
    
    window.location.href = `mailto:connect@rivontrix.com?subject=${subject}&body=${body}`;
    
    // Reset form after a short delay
    setTimeout(() => {
      setResumeForm({
        name: '',
        email: '',
        phone: '',
        position: '',
        message: ''
      });
      setResumeFile(null);
      document.getElementById('resume-form').reset();
      setShowResumeForm(false);
      document.body.style.overflow = 'auto';
    }, 1000);
  };

  return (
    <>
      <PageHero
        sectionId="careers"
        title="Join Our Team"
        subtitle="Build the Future with Rivontrix"
        description="Be part of a next-generation technology firm where innovation meets expertise. Explore exciting career opportunities in cybersecurity, AI, cloud computing, and digital transformation."
        image="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&q=90&auto=format"
        gradient="linear-gradient(135deg, rgba(102, 126, 234, 0.9), rgba(118, 75, 162, 0.9))"
      />
      <section id="careers" className="careers">
        <div className="container">
          <div className="careers-content">
            <div className="careers-intro">
              <h2 className="section-title">Why Work at Rivontrix?</h2>
              <p className="section-description">
                At Rivontrix, we're not just building technology—we're shaping the future of enterprise security and digital innovation. 
                Join a team that values creativity, expertise, and cutting-edge solutions.
              </p>
            </div>

            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-lightbulb"></i>
                </div>
                <h3>Innovation Culture</h3>
                <p>Work on cutting-edge GenAI, cybersecurity, and cloud technologies that define the industry.</p>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-users"></i>
                </div>
                <h3>Expert Team</h3>
                <p>Collaborate with industry leaders and domain experts in security, AI, and digital transformation.</p>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h3>Career Growth</h3>
                <p>Advance your career with continuous learning opportunities and mentorship programs.</p>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-globe"></i>
                </div>
                <h3>Global Impact</h3>
                <p>Make a difference by protecting enterprises worldwide with innovative security solutions.</p>
              </div>
            </div>

            <div className="open-positions">
              <h2 className="section-title">Open Positions</h2>
              <p className="section-description">
                We're always looking for talented individuals to join our team. If you don't see a position that matches your skills, 
                we'd still love to hear from you.
              </p>

              <div className="positions-coming-soon">
                <div className="coming-soon-card">
                  <i className="fas fa-briefcase"></i>
                  <h3>Open Positions Coming Soon</h3>
                  <p>We're actively building our team. Check back soon or submit your resume to be considered for future opportunities.</p>
                  <button 
                    className="btn btn-primary open-resume-form-btn"
                    onClick={() => {
                      setShowResumeForm(true);
                      document.body.style.overflow = 'hidden';
                    }}
                  >
                    <i className="fas fa-paper-plane"></i>
                    Submit Resume
                  </button>
                </div>
              </div>
            </div>

            {showResumeForm && (
              <div className="resume-modal-overlay" onClick={() => {
                setShowResumeForm(false);
                setResumeForm({
                  name: '',
                  email: '',
                  phone: '',
                  position: '',
                  message: ''
                });
                setResumeFile(null);
              }}>
                <div id="resume-submission-section" className="resume-submission modal" onClick={(e) => e.stopPropagation()}>
                  <h2 className="section-title">Submit Your Resume</h2>
                  <p className="section-description">
                    Interested in joining our team? Send us your resume and we'll keep it on file for future opportunities that match your skills and experience.
                  </p>

              <form id="resume-form" className="resume-form" onSubmit={handleResumeSubmit}>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={resumeForm.name}
                      onChange={handleFormChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={resumeForm.email}
                      onChange={handleFormChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={resumeForm.phone}
                      onChange={handleFormChange}
                      placeholder="+65 XXXX XXXX"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="position">Position of Interest</label>
                    <input
                      type="text"
                      id="position"
                      name="position"
                      value={resumeForm.position}
                      onChange={handleFormChange}
                      placeholder="e.g., Cybersecurity Specialist, AI Engineer, etc."
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="resume">Upload Resume *</label>
                  <div className="file-upload-wrapper">
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      required
                      className="file-input"
                    />
                    <label htmlFor="resume" className="file-label">
                      <i className="fas fa-cloud-upload-alt"></i>
                      <span>{resumeFile ? resumeFile.name : 'Choose file (PDF or Word, Max 10MB)'}</span>
                      {resumeFile && (
                        <span className="file-size">
                          ({(resumeFile.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      )}
                    </label>
                  </div>
                  <small className="file-hint">
                    Accepted formats: PDF, DOC, DOCX | Maximum file size: 10MB
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Additional Information (Optional)</label>
                  <textarea
                    id="message"
                    name="message"
                    value={resumeForm.message}
                    onChange={handleFormChange}
                    placeholder="Tell us about your experience, skills, or why you're interested in joining Rivontrix..."
                    rows="4"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary submit-resume-btn"
                >
                  <span>Open Email Client</span>
                  <i className="fas fa-envelope"></i>
                </button>

                <p className="form-note">
                  <i className="fas fa-info-circle"></i>
                  Clicking "Open Email Client" will open your default email application. Please attach your resume file before sending.
                </p>
              </form>
              
                  <button 
                    className="btn-close-form"
                    onClick={() => {
                      setShowResumeForm(false);
                      document.body.style.overflow = 'auto';
                      setResumeForm({
                        name: '',
                        email: '',
                        phone: '',
                        position: '',
                        message: ''
                      });
                      setResumeFile(null);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Careers;

